import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { api, authFetch } from "../utils/api";
import { t } from "../utils/i18n";
import { X, Settings, SlidersHorizontal, RefreshCcw, Grid3x3, Check, Camera, CalendarDays, Repeat, Users, MonitorPlay, ChevronLeft } from "lucide-react";
import MediaStoryCard from "./MediaStoryCard";
import { PlatformLogo, PROVIDER_META, PROVIDER_ORDER, MediaProviderId } from "./MediaPlatformIcons";
import RealVibeMoodSheet from "./RealVibeMoodSheet";
import { MOOD_ICONS, moodLabelKey } from "../utils/moodKeys";

const LINK_LABELS: Record<string, string> = { publication: "Événement", ritual: "Rituel", community: "Communauté" };
const MODE_MAX_MS: Record<"vibe" | "live", number> = { vibe: 10000, live: 30000 };
// Composant partagé entre l'aperçu (étape 3) et le viewer plein écran des stories (Stories.tsx) —
// même mise en page BeReal : photo arrière plein cadre + selfie avant en médaillon coin haut-gauche.
export function RealComposite({ backUrl, frontUrl }: { backUrl: string; frontUrl: string }) {
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <img src={backUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} draggable={false} />
      <div style={{
        position: "absolute", top: 16, left: 16, width: "28%", aspectRatio: "3/4",
        borderRadius: 14, overflow: "hidden", border: "2.5px solid white",
        boxShadow: "0 3px 12px rgba(0,0,0,0.35)",
      }}>
        <img src={frontUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} draggable={false} />
      </div>
    </div>
  );
}

const CHOICE_META: Record<"publication" | "ritual" | "community", { icon: typeof CalendarDays; label: string }> = {
  publication: { icon: CalendarDays, label: "Événement" },
  ritual: { icon: Repeat, label: "Rituel" },
  community: { icon: Users, label: "Communauté" },
};

// Carte de choix noire premium (Événement/Rituel/Communauté) — remplace les pills neutres :
// icône + libellé, plus grande, plus contrastée. Une fois un choix fait, les deux autres
// s'estompent et la sélectionnée gagne un liseré + un badge check, pour rester lisible même
// avec les 3 en noir par défaut (contrairement aux pills où seule la sélectionnée était noire).
function ChoiceCard({ type, active, dimmed, onClick }: { type: "publication" | "ritual" | "community"; active: boolean; dimmed: boolean; onClick: () => void }) {
  const { icon: Icon, label } = CHOICE_META[type];
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, height: 88, borderRadius: 16, border: "none", cursor: "pointer",
        backgroundColor: "#0a0a0a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
        opacity: dimmed ? 0.38 : 1,
        boxShadow: active ? "0 0 0 2px #FFFFFF inset, 0 0 0 2.5px #0a0a0a, 0 8px 20px rgba(10,10,10,0.28)" : "0 3px 10px rgba(10,10,10,0.16)",
        position: "relative", transition: "opacity 0.15s, box-shadow 0.15s",
      }}
    >
      <Icon size={23} strokeWidth={1.9} color="white" />
      <span style={{ fontSize: 12.5, fontWeight: 700, color: "white" }}>{label}</span>
      {active && (
        <span style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: 9, backgroundColor: "white", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }}>
          <Check size={12} strokeWidth={3} color="#0a0a0a" />
        </span>
      )}
    </button>
  );
}

const FILTERS: { id: string; label: string; css: string; swatch: string; beauty?: boolean }[] = [
  { id: "normal", label: "Normal", css: "none", swatch: "#9AA0A6" },
  { id: "beaute", label: "Beauté", css: "brightness(1.1) contrast(0.96) saturate(1.04)", swatch: "#F7C6D9", beauty: true },
  { id: "lumineux", label: "Lumineux", css: "brightness(1.18) contrast(1.05) saturate(1.05)", swatch: "#F5D76E" },
  { id: "nb", label: "N&B", css: "grayscale(1) contrast(1.05)", swatch: "#4a4a4a" },
  { id: "vif", label: "Vif", css: "saturate(1.7) contrast(1.15)", swatch: "#E0367A" },
  { id: "vintage", label: "Vintage", css: "sepia(0.35) contrast(0.9) brightness(1.05) saturate(0.8)", swatch: "#B98B4E" },
  { id: "froid", label: "Froid", css: "hue-rotate(-8deg) saturate(1.15) brightness(1.03) contrast(1.05)", swatch: "#3E7CB1" },
  { id: "chaud", label: "Chaud", css: "sepia(0.22) saturate(1.35) brightness(1.05)", swatch: "#D2691E" },
];

type Step = "context" | "camera" | "preview" | "media";

// Modale d'enregistrement d'un Live/Vibe — caméra plein écran façon Instagram : filtres appliqués
// en direct via canvas (donc réellement encodés dans la vidéo, pas juste un effet CSS de preview),
// bascule avant/arrière, mode Vibe (10s)/Live (30s) au même endroit puisque c'est la même techno,
// toujours rattachée à une activité en cours (contrainte déjà imposée côté backend). Aussi
// réutilisée pour la création de "vibe" (Stories) depuis la page d'accueil — kind="story" ne
// change que le titre, le mode par défaut (vibe) et où la publication atterrit (POST /stories
// avec légende, au lieu de POST /users/:id/photos) ; le reste du rendu est strictement identique.
export default function LiveRecordModal({ onClose, onCreated, kind = "live" }: { onClose: () => void; onCreated: (item: any) => void; kind?: "live" | "story" }) {
  const { user, lang } = useApp();
  const [step, setStep] = useState<Step>("context");
  const [linkType, setLinkType] = useState<"publication" | "ritual" | "community" | "">("");
  const [linkOptions, setLinkOptions] = useState<{ id: string; label: string }[]>([]);
  const [linkId, setLinkId] = useState("");
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [caption, setCaption] = useState("");

  // Lieu tagué (optionnel) — indépendant du lien à une activité ci-dessus : un Real n'a jamais
  // d'activité liée mais peut désormais être tagué à un lieu ; un Vibe/Live peut l'être en plus
  // de son activité. Recherche uniquement les lieux OLTODO certifiés (pas Nominatim, pas de
  // création inline) : venue_id référence la table venues, pas une adresse libre.
  const [venueQuery, setVenueQuery] = useState("");
  const [venueResults, setVenueResults] = useState<{ id: string; name: string }[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<{ id: string; name: string } | null>(null);
  const [showVenueResults, setShowVenueResults] = useState(false);
  const venueSearchSeq = useRef(0);

  useEffect(() => {
    const mySeq = ++venueSearchSeq.current;
    if (venueQuery.length < 2) { setVenueResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/venues/search?q=${encodeURIComponent(venueQuery)}`)
        .then(d => { if (mySeq === venueSearchSeq.current) setVenueResults(d?.venues || []); })
        .catch(() => { if (mySeq === venueSearchSeq.current) setVenueResults([]); });
    }, 350);
    return () => clearTimeout(timer);
  }, [venueQuery]);

  const [mode, setMode] = useState<"vibe" | "live" | "real">(kind === "story" ? "vibe" : "live");
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [filterId, setFilterId] = useState("normal");
  const [showFilters, setShowFilters] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [streamReady, setStreamReady] = useState(false);

  const [recording, setRecording] = useState(false);
  const [recordMs, setRecordMs] = useState(0);
  const [preview, setPreview] = useState<{ url: string; blob: Blob; durationSeconds: number } | null>(null);
  const [cameraError, setCameraError] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Real — capture séquentielle de deux photos (dos puis selfie), pas de MediaRecorder.
  const [realStatus, setRealStatus] = useState<{ loaded: boolean; postedToday: boolean }>({ loaded: false, postedToday: false });
  const [realBackShot, setRealBackShot] = useState<{ blob: Blob; url: string } | null>(null);
  const [realFrontShot, setRealFrontShot] = useState<{ blob: Blob; url: string } | null>(null);
  const [realCapturing, setRealCapturing] = useState(false);
  const awaitingFrontRef = useRef(false);

  // Mood optionnel attaché au Real Vibe (voir RealVibeMoodSheet.tsx) — 'invite_me' + envie
  // facultative, ou une simple émotion. Jamais requis pour publier.
  const [moodKey, setMoodKey] = useState<string | null>(null);
  const [moodWishKey, setMoodWishKey] = useState<string | null>(null);
  const [showMoodSheet, setShowMoodSheet] = useState(false);

  // Media Vibe — raccourci frère du Real : ce que l'utilisateur regarde/écoute en ce moment,
  // jamais lié à une activité. YouTube/Spotify/Deezer auto-remplissent titre+vignette via
  // oEmbed (proxy serveur) ; Netflix/HBO/Prime/Canal+ n'ont aucune API publique, saisie
  // manuelle du titre uniquement.
  const [mediaProvider, setMediaProvider] = useState<MediaProviderId | null>(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaTitle, setMediaTitle] = useState("");
  const [mediaThumb, setMediaThumb] = useState<string | null>(null);
  const [mediaFetching, setMediaFetching] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const mediaFetchSeq = useRef(0);

  const videoLiveRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const blurCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordStartRef = useRef(0);
  const drawCountRef = useRef(0);

  const activeFilter = useMemo(() => FILTERS.find(f => f.id === filterId) || FILTERS[0], [filterId]);
  const maxMs = MODE_MAX_MS[mode as "vibe" | "live"] || MODE_MAX_MS.vibe;

  useEffect(() => {
    if (!linkType || !user?.id) { setLinkOptions([]); setLinkId(""); return; }
    setLoadingLinks(true);
    setLinkId("");
    const load = linkType === "publication"
      ? api.get(`/users/${user.id}/publications`).then(d => (d?.publications || []).filter((p: any) => p.type !== "feedback").map((p: any) => ({ id: p.id, label: p.title })))
      : linkType === "ritual"
      ? api.get("/rituals/me").then(d => (d?.rituals || []).map((r: any) => ({ id: r.id, label: r.title })))
      : api.get(`/users/${user.id}/communities`).then(d => (d?.communities || []).map((c: any) => ({ id: c.id, label: c.name })));
    load.then(setLinkOptions).catch(() => setLinkOptions([])).finally(() => setLoadingLinks(false));
  }, [linkType, user?.id]);

  const hasActivity = !!linkType && !!linkId;

  // Statut du Real du jour — appelé au montage pour désactiver le bouton voyant plutôt que de
  // laisser l'utilisateur faire la double capture pour rien si le backend la refusera (1/jour).
  useEffect(() => {
    api.get("/stories/real/status")
      .then(d => setRealStatus({ loaded: true, postedToday: !!d?.posted_today }))
      .catch(() => setRealStatus({ loaded: true, postedToday: false }));
  }, []);

  // Ouvre la caméra dès qu'on entre en mode plein écran, et la ré-ouvre à chaque bascule avant/arrière.
  useEffect(() => {
    if (step !== "camera") return;
    let cancelled = false;
    setStreamReady(false);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: { facingMode } });
        if (cancelled) { stream.getTracks().forEach(tr => tr.stop()); return; }
        streamRef.current = stream;
        if (videoLiveRef.current) {
          videoLiveRef.current.srcObject = stream;
          await videoLiveRef.current.play().catch(() => {});
        }
        setCameraError("");
        setStreamReady(true);
      } catch {
        setCameraError("Caméra/micro indisponible ou autorisation refusée");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(tr => tr.stop());
      streamRef.current = null;
      setStreamReady(false);
    };
  }, [step, facingMode]);

  // Boucle de dessin : la caméra brute est peinte sur le canvas avec le filtre CSS actif — c'est
  // donc ce canvas (et son captureStream) qui est à la fois affiché et enregistré, le filtre est
  // ainsi réellement encodé dans la vidéo finale, pas juste un habillage visuel de la preview.
  useEffect(() => {
    if (step !== "camera" || !streamReady) return;
    drawCountRef.current = 0;
    let raf = 0;
    const draw = () => {
      const video = videoLiveRef.current, canvas = canvasRef.current;
      if (video && canvas && video.readyState >= 2) {
        if (canvas.width !== video.videoWidth && video.videoWidth) canvas.width = video.videoWidth;
        if (canvas.height !== video.videoHeight && video.videoHeight) canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (ctx && canvas.width && canvas.height) {
          drawCountRef.current += 1;
          ctx.save();
          if (facingMode === "user") {
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
          }
          ctx.filter = activeFilter.css;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          // "Beauté" — lissage du grain de peau par diffusion douce : on peint une version
          // fortement réduite (donc floue une fois réagrandie) par-dessus, semi-transparente.
          // Vraiment recomposé dans le canvas (donc encodé dans la vidéo), pas un filtre CSS de façade.
          if (activeFilter.beauty) {
            if (!blurCanvasRef.current) blurCanvasRef.current = document.createElement("canvas");
            const blurCanvas = blurCanvasRef.current;
            const bw = 48, bh = Math.max(1, Math.round((bw * canvas.height) / canvas.width));
            if (blurCanvas.width !== bw) blurCanvas.width = bw;
            if (blurCanvas.height !== bh) blurCanvas.height = bh;
            const bctx = blurCanvas.getContext("2d");
            if (bctx) {
              bctx.filter = activeFilter.css;
              bctx.drawImage(video, 0, 0, bw, bh);
              ctx.globalAlpha = 0.45;
              ctx.drawImage(blurCanvas, 0, 0, canvas.width, canvas.height);
              ctx.globalAlpha = 1;
            }
          }
          ctx.restore();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [step, streamReady, activeFilter, facingMode]);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Capture une image du canvas (donc filtre + mise en miroir déjà "cuits" dedans, comme pour la vidéo).
  const captureCanvasBlob = (): Promise<Blob | null> => new Promise(resolve => {
    const canvas = canvasRef.current;
    if (!canvas) { resolve(null); return; }
    canvas.toBlob(b => resolve(b), "image/jpeg", 0.92);
  });

  // Une fois la photo arrière prise, on bascule sur la caméra avant ; dès que ce nouveau flux est
  // prêt (réutilise le même signal streamReady que l'effet d'ouverture de caméra ci-dessus), on
  // attend que quelques frames FRAÎCHES du nouveau flux aient vraiment été peintes sur le canvas
  // (compteur incrémenté par la boucle de dessin ci-dessus, remis à zéro à chaque réouverture de
  // caméra) avant de capturer le selfie automatiquement — un délai fixe s'est avéré insuffisant
  // sur une bascule caméra lente (le capteur peut mettre plusieurs centaines de ms à livrer sa
  // première image), ce qui recopiait sinon la photo arrière comme selfie. Plafonné pour ne
  // jamais bloquer indéfiniment si quelque chose empêche le dessin.
  useEffect(() => {
    if (mode !== "real" || step !== "camera" || !awaitingFrontRef.current || !streamReady) return;
    awaitingFrontRef.current = false;
    let cancelled = false;
    const startCount = drawCountRef.current;
    const startedAt = performance.now();
    const MIN_FRESH_FRAMES = 6;
    const MAX_WAIT_MS = 1500;
    const waitForFreshFrame = () => new Promise<void>(resolve => {
      const check = () => {
        if (cancelled) { resolve(); return; }
        if (drawCountRef.current - startCount >= MIN_FRESH_FRAMES || performance.now() - startedAt >= MAX_WAIT_MS) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    });
    (async () => {
      await waitForFreshFrame();
      if (cancelled) return;
      const blob = await captureCanvasBlob();
      if (cancelled) return;
      if (!blob) {
        setCameraError("La capture du selfie a échoué, réessaie");
        setRealCapturing(false);
        return;
      }
      setRealFrontShot({ blob, url: URL.createObjectURL(blob) });
      setRealCapturing(false);
      setStep("preview");
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, step, streamReady]);

  const captureRealShot = async () => {
    if (!streamReady || realCapturing || realBackShot) return;
    const blob = await captureCanvasBlob();
    if (!blob) { setCameraError("La capture a échoué, réessaie"); return; }
    setRealBackShot({ blob, url: URL.createObjectURL(blob) });
    setRealCapturing(true);
    setCameraError("");
    awaitingFrontRef.current = true;
    setFacingMode("user");
  };

  const resetReal = () => {
    if (realBackShot) URL.revokeObjectURL(realBackShot.url);
    if (realFrontShot) URL.revokeObjectURL(realFrontShot.url);
    setRealBackShot(null);
    setRealFrontShot(null);
    setRealCapturing(false);
    awaitingFrontRef.current = false;
    setMoodKey(null);
    setMoodWishKey(null);
  };

  const startReal = () => {
    if (realStatus.postedToday) return;
    resetReal();
    setMode("real");
    setFacingMode("environment");
    setStep("camera");
  };

  const resetMedia = () => {
    setMediaProvider(null);
    setMediaUrl("");
    setMediaTitle("");
    setMediaThumb(null);
    setMediaFetching(false);
    setMediaError("");
  };

  const startMedia = () => {
    resetMedia();
    setStep("media");
  };

  // Fetch auto-débouncé de titre+vignette pour les 3 plateformes à oEmbed public — le même
  // garde anti-course que la recherche de lieu (venueSearchSeq) plus haut : une réponse en
  // retard pour une URL déjà remplacée par une autre ne doit jamais écraser l'état courant.
  useEffect(() => {
    if (!mediaProvider || !PROVIDER_META[mediaProvider].oembed) return;
    const mySeq = ++mediaFetchSeq.current;
    if (mediaUrl.trim().length < 8) { setMediaThumb(null); return; }
    setMediaError("");
    const timer = setTimeout(() => {
      setMediaFetching(true);
      api.get(`/stories/media-metadata?provider=${mediaProvider}&url=${encodeURIComponent(mediaUrl.trim())}`)
        .then(d => {
          if (mySeq !== mediaFetchSeq.current) return;
          if (d?.title) { setMediaTitle(d.title); setMediaThumb(d.thumbnail_url || null); }
          else { setMediaThumb(null); setMediaError("Lien non reconnu, vérifie qu'il pointe bien vers ce contenu"); }
        })
        .catch(() => { if (mySeq === mediaFetchSeq.current) setMediaThumb(null); })
        .finally(() => { if (mySeq === mediaFetchSeq.current) setMediaFetching(false); });
    }, 600);
    return () => clearTimeout(timer);
  }, [mediaProvider, mediaUrl]);

  const publishMedia = async () => {
    if (!mediaProvider || publishing) return;
    setPublishing(true);
    setMediaError("");
    try {
      const created = await api.post("/stories", {
        kind: "media", external_provider: mediaProvider,
        external_url: mediaUrl.trim() || undefined, external_title: mediaTitle.trim() || undefined,
        caption: caption.trim() || undefined, venue_id: selectedVenue?.id || undefined,
      });
      if (created?.story) onCreated(created.story);
      else setMediaError(created?.error || "Erreur lors de la publication");
    } catch (e: any) {
      setMediaError(e?.message || "Erreur de connexion");
    } finally {
      setPublishing(false);
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
    setRecording(false);
  };

  const startRecording = () => {
    if (recording || !hasActivity || !canvasRef.current || !streamRef.current) return;
    const canvasStream = (canvasRef.current as any).captureStream(30) as MediaStream;
    const micTracks = streamRef.current.getAudioTracks();
    const combined = new MediaStream([...canvasStream.getVideoTracks(), ...micTracks]);
    const mimeType = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
      .find(t => (window as any).MediaRecorder?.isTypeSupported?.(t)) || "";
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(combined, mimeType ? { mimeType } : undefined);
    } catch {
      setCameraError("L'enregistrement vidéo n'est pas supporté par ce navigateur");
      return;
    }
    chunksRef.current = [];
    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const durationSeconds = Math.max(1, Math.round((Date.now() - recordStartRef.current) / 1000));
      const blob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
      if (blob.size > 500) {
        setPreview({ url: URL.createObjectURL(blob), blob, durationSeconds });
        setStep("preview");
      } else {
        setCameraError("L'enregistrement a échoué, réessaie");
      }
    };
    recorder.start();
    mediaRecorderRef.current = recorder;
    recordStartRef.current = Date.now();
    setRecording(true);
    setRecordMs(0);
    timerRef.current = setInterval(() => {
      setRecordMs(ms => {
        const next = ms + 100;
        if (next >= maxMs) { stopRecording(); return maxMs; }
        return next;
      });
    }, 100);
  };

  const retake = () => {
    if (mode === "real") {
      // Pas de retake partiel pour v1 — on refait systématiquement les deux photos.
      resetReal();
      setFacingMode("environment");
      setStep("camera");
      return;
    }
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setRecordMs(0);
    setStep("camera");
  };

  const publishReal = async () => {
    if (!realBackShot || !realFrontShot || !user?.id || publishing) return;
    setPublishing(true);
    setCameraError("");
    try {
      const uploadOne = async (blob: Blob, name: string) => {
        const fd = new FormData();
        fd.append("file", new File([blob], name, { type: blob.type || "image/jpeg" }));
        const res = await authFetch("/uploads/story", { method: "POST", body: fd });
        if (!res.ok) throw new Error(`Échec de l'envoi (${res.status})`);
        const data = await res.json();
        if (!data?.url) throw new Error("Erreur lors de l'envoi");
        return data.url as string;
      };
      const backUrl = await uploadOne(realBackShot.blob, "real-back.jpg");
      const frontUrl = await uploadOne(realFrontShot.blob, "real-front.jpg");
      const created = await api.post("/stories", {
        media_url: backUrl, secondary_media_url: frontUrl, media_type: "image",
        kind: "real", caption: caption.trim() || undefined, venue_id: selectedVenue?.id || undefined,
        mood_key: moodKey || undefined,
        mood_wish_key: moodKey === "invite_me" ? (moodWishKey || undefined) : undefined,
      });
      if (created?.story) onCreated(created.story);
      else setCameraError(created?.error || t(lang, "real_vibe_publish_error"));
    } catch (e: any) {
      setCameraError(e?.message || "Erreur de connexion");
    } finally {
      setPublishing(false);
    }
  };

  const publish = async () => {
    if (mode === "real") { await publishReal(); return; }
    if (!preview || !user?.id || publishing) return;
    setPublishing(true);
    setCameraError("");
    try {
      const fd = new FormData();
      const ext = preview.blob.type.includes("mp4") ? "mp4" : "webm";
      fd.append("file", new File([preview.blob], `live.${ext}`, { type: preview.blob.type || "video/webm" }));
      const uploadPath = kind === "story" ? "/uploads/story" : "/uploads/profile-gallery";
      const res = await authFetch(uploadPath, { method: "POST", body: fd });
      if (!res.ok) { setCameraError(`Échec de l'envoi (${res.status})`); return; }
      const data = await res.json();
      if (!data?.url) { setCameraError("Erreur lors de l'envoi"); return; }
      if (kind === "story") {
        const created = await api.post("/stories", {
          media_url: data.url, media_type: "video", caption: caption.trim() || undefined,
          link_type: linkType, link_id: linkId, venue_id: selectedVenue?.id || undefined,
        });
        if (created?.story) onCreated(created.story);
        else setCameraError(created?.error || "Erreur lors de la publication de la vibe");
      } else {
        const created = await api.post(`/users/${user.id}/photos`, {
          url: data.url, media_type: "video", duration_seconds: preview.durationSeconds,
          link_type: linkType, link_id: linkId, venue_id: selectedVenue?.id || undefined,
        });
        if (created?.photo) onCreated(created.photo);
        else setCameraError(created?.error || "Erreur lors de la publication du Live");
      }
    } catch (e: any) {
      setCameraError(e?.message ? `Erreur de connexion : ${e.message}` : "Erreur de connexion");
    } finally {
      setPublishing(false);
    }
  };

  const handleCloseAttempt = () => {
    if (step === "preview" || (step === "camera" && recordMs > 0) || (step === "camera" && mode === "real" && (realBackShot || realCapturing)) || (step === "media" && (!!mediaUrl || !!mediaTitle))) setConfirmDiscard(true);
    else onClose();
  };
  const confirmAbandon = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    resetReal();
    onClose();
  };

  // Étape 1 — choix de l'activité en cours, avant d'ouvrir la caméra.
  if (step === "context") {
    // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
    // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
    // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
    return ReactDOM.createPortal(
      <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 400, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 17, fontWeight: 700 }}>{kind === "story" ? "Nouvelle vibe" : "Nouveau Live"}</span>
            <button onClick={onClose} aria-label="Fermer" style={{ border: "none", background: "none", cursor: "pointer", padding: 4, color: "#999", display: "flex" }}><X size={20} strokeWidth={2} /></button>
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: "#666", marginBottom: 20 }}>Ce que tu vis en ce moment</div>

          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            {(["publication", "ritual", "community"] as const).map(t => (
              <ChoiceCard key={t} type={t} active={linkType === t} dimmed={!!linkType && linkType !== t} onClick={() => setLinkType(t)} />
            ))}
          </div>
          {linkType && (
            loadingLinks ? (
              <div style={{ fontSize: 13, color: "#999", marginBottom: 14 }}>Chargement...</div>
            ) : linkOptions.length === 0 ? (
              <div style={{ fontSize: 13, color: "#999", marginBottom: 14 }}>Rien à lier pour l'instant, crée d'abord {linkType === "publication" ? "un événement" : linkType === "ritual" ? "un rituel" : "une communauté"}.</div>
            ) : (
              <select value={linkId} onChange={e => setLinkId(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "white", boxSizing: "border-box" }}>
                <option value="">Choisir...</option>
                {linkOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            )
          )}

          {/* Lieu tagué (optionnel) — s'applique aussi bien au Real (bouton ci-dessus) qu'au
              Vibe/Live (activité ci-dessus) : les followers du lieu seront notifiés et le
              contenu apparaîtra sur sa fiche / sur la carte Explorer. */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8, marginTop: 4 }}>Taguer un lieu (optionnel)</div>
          <div style={{ position: "relative", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, border: `1px solid ${selectedVenue ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: selectedVenue ? "#F0F0F0" : "#F8F8F8" }}>
              <input
                value={venueQuery}
                onChange={e => { setVenueQuery(e.target.value); setSelectedVenue(null); setShowVenueResults(e.target.value.length > 1); }}
                onFocus={() => venueQuery.length > 1 && setShowVenueResults(true)}
                placeholder="Rechercher un lieu OLTODO..."
                style={{ flex: 1, border: "none", outline: "none", fontSize: 14, backgroundColor: "transparent", color: "#0a0a0a" }}
              />
              {selectedVenue && <span style={{ color: "#0a0a0a", fontWeight: 700 }}>✓</span>}
              {venueQuery && (
                <button onClick={() => { setVenueQuery(""); setSelectedVenue(null); setVenueResults([]); setShowVenueResults(false); }} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 16, lineHeight: 1 }}>✕</button>
              )}
            </div>
            {showVenueResults && venueResults.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", zIndex: 10, maxHeight: 200, overflowY: "auto" }}>
                {venueResults.map(v => (
                  <div key={v.id} onClick={() => { setSelectedVenue(v); setVenueQuery(v.name); setShowVenueResults(false); }} style={{ padding: "11px 14px", cursor: "pointer", fontSize: 13.5, borderBottom: "1px solid #F5F5F5" }}>
                    {v.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => hasActivity && setStep("camera")} disabled={!hasActivity} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: hasActivity ? "pointer" : "default", opacity: hasActivity ? 1 : 0.4 }}>
            Continuer
          </button>

          {kind === "story" && (
            <>
              {/* Bouton voyant — Real n'exige aucun lien à une activité ci-dessus ; il saute
                  directement à l'étape caméra. Désactivé + message inline si déjà posté
                  aujourd'hui (statut vérifié au montage via GET /stories/real/status). */}
              <button
                onClick={startReal}
                disabled={!realStatus.loaded || realStatus.postedToday}
                style={{
                  width: "100%", marginTop: 18, padding: "14px 16px", borderRadius: 14, border: "none",
                  display: "flex", alignItems: "center", gap: 12, textAlign: "left" as const,
                  cursor: realStatus.postedToday ? "default" : "pointer",
                  background: realStatus.postedToday ? "#F0F0F0" : "linear-gradient(135deg, #FF3B30, #FF7A45)",
                  boxShadow: realStatus.postedToday ? "none" : "0 6px 16px rgba(255,59,48,0.32)",
                  opacity: realStatus.loaded ? 1 : 0.6,
                }}
              >
                <span style={{ width: 42, height: 42, borderRadius: 13, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: realStatus.postedToday ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.22)" }}>
                  <Camera size={21} strokeWidth={2.2} color={realStatus.postedToday ? "#999" : "white"} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: realStatus.postedToday ? "#999" : "white" }}>{t(lang, "real_vibe_share_cta")}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 500, color: realStatus.postedToday ? "#999" : "rgba(255,255,255,0.92)" }}>
                    {realStatus.postedToday ? t(lang, "real_vibe_already_posted") : t(lang, "real_vibe_share_sub")}
                  </div>
                </span>
              </button>

              {/* Media Vibes — partage instantané de ce qu'on regarde/écoute, même registre que
                  Real (raccourci sans activité liée) mais identité visuelle distincte : dégradé
                  violet dominant + jaune + rouge, logos monochromes des plateformes plutôt qu'un
                  gros texte (les logos suffisent à comprendre de quoi il s'agit). */}
              <button
                onClick={startMedia}
                aria-label="Media Vibes, partager ce que tu regardes ou écoutes"
                style={{
                  width: "100%", marginTop: 10, padding: "14px 16px", borderRadius: 14, border: "none",
                  display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                  background: "linear-gradient(135deg, #5B2FD6 0%, #8B45E8 45%, #D9435C 80%, #F0B441 100%)",
                  boxShadow: "0 6px 16px rgba(91,47,214,0.30)",
                }}
              >
                <span style={{ width: 42, height: 42, borderRadius: 13, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(255,255,255,0.22)" }}>
                  <MonitorPlay size={21} strokeWidth={2.2} color="white" />
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: "left" as const }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    {PROVIDER_ORDER.map(id => <PlatformLogo key={id} id={id} size={16} color="rgba(255,255,255,0.92)" />)}
                  </span>
                  <span style={{ display: "block", marginTop: 5, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" as const, color: "rgba(255,255,255,0.72)" }}>Media Vibes</span>
                </span>
              </button>
            </>
          )}
        </div>
      </div>,
      document.body
    );
  }

  const iconBtnStyle: React.CSSProperties = { width: 42, height: 42, borderRadius: 21, border: "none", backgroundColor: "rgba(0,0,0,0.4)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };

  // Étape "media" — Media Vibes : choix de la plateforme, puis lien (auto-rempli via oEmbed)
  // ou titre saisi à la main selon la plateforme, avec aperçu en direct.
  if (step === "media") {
    const meta = mediaProvider ? PROVIDER_META[mediaProvider] : null;
    const canPublish = !!mediaProvider && (meta?.oembed ? !!mediaTitle && !mediaFetching : !!mediaTitle.trim());
    // Portail direct sur document.body — voir le commentaire de l'étape "context" ci-dessus.
    return ReactDOM.createPortal(
      <div onClick={handleCloseAttempt} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 400, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <button onClick={() => (mediaProvider ? setMediaProvider(null) : setStep("context"))} aria-label="Retour" style={{ border: "none", background: "none", cursor: "pointer", padding: 4, color: "#0a0a0a", display: "flex" }}><ChevronLeft size={22} strokeWidth={2.2} /></button>
            <span style={{ fontSize: 17, fontWeight: 700 }}>Media Vibes</span>
          </div>

          {!mediaProvider ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {PROVIDER_ORDER.map(id => (
                <button key={id} onClick={() => setMediaProvider(id)} style={{ aspectRatio: "1", borderRadius: 16, border: "1.5px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <PlatformLogo id={id} size={26} color="#0a0a0a" />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#666" }}>{PROVIDER_META[id].label}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              {meta!.oembed ? (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>Colle le lien {meta!.label}</div>
                  <input
                    value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder={`https://...${PROVIDER_META[mediaProvider].label.toLowerCase()}...`}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 8, backgroundColor: "white", boxSizing: "border-box" }}
                  />
                  {mediaFetching && <div style={{ fontSize: 12.5, color: "#999", marginBottom: 10 }}>Récupération des infos...</div>}
                  {mediaError && <div style={{ fontSize: 12.5, color: "#C0392B", marginBottom: 10 }}>{mediaError}</div>}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>Qu'est-ce que tu regardes sur {meta!.label} ?</div>
                  <input
                    value={mediaTitle} onChange={e => setMediaTitle(e.target.value)} placeholder="Ex : Stranger Things"
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 14, backgroundColor: "white", boxSizing: "border-box" }}
                  />
                </>
              )}

              {(mediaTitle || mediaFetching) && (
                <div style={{ width: "100%", aspectRatio: "3/4", borderRadius: 16, overflow: "hidden", marginTop: 4, marginBottom: 18 }}>
                  <MediaStoryCard provider={mediaProvider} title={mediaTitle || "..."} thumbnailUrl={mediaThumb} url={null} renderCta={false} />
                </div>
              )}

              <button onClick={publishMedia} disabled={!canPublish || publishing} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: canPublish ? "pointer" : "default", opacity: canPublish && !publishing ? 1 : 0.4 }}>
                {publishing ? "Publication..." : "Partager"}
              </button>
            </>
          )}
        </div>
      </div>,
      document.body
    );
  }

  // Étape 3 — preview de ce qui vient d'être filmé (ou des deux photos pour un Real).
  if (step === "preview" && mode === "real" && realBackShot && realFrontShot) {
    // Portail direct sur document.body — voir le commentaire de l'étape "context" plus haut.
    return ReactDOM.createPortal(
      <div style={{ position: "fixed", inset: 0, backgroundColor: "#000", zIndex: 2000, display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, minHeight: 0 }}><RealComposite backUrl={realBackShot.url} frontUrl={realFrontShot.url} /></div>
        <button onClick={handleCloseAttempt} aria-label="Fermer" style={{ ...iconBtnStyle, position: "absolute", top: "max(16px, env(safe-area-inset-top))", left: 16 }}><X size={20} strokeWidth={2.2} /></button>
        {cameraError && <div style={{ position: "absolute", top: 70, left: 16, right: 16, fontSize: 12.5, color: "white", backgroundColor: "rgba(192,57,43,0.9)", padding: "8px 12px", borderRadius: 10 }}>{cameraError}</div>}
        <div style={{ padding: "0 16px 10px", backgroundColor: "#000" }}>
          <input value={caption} onChange={e => setCaption(e.target.value)} maxLength={150} placeholder="Une légende (optionnel)..." style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.25)", backgroundColor: "rgba(255,255,255,0.08)", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", gap: 10, padding: "14px 16px max(14px, env(safe-area-inset-bottom))", backgroundColor: "#000" }}>
          <button onClick={retake} disabled={publishing} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.3)", backgroundColor: "transparent", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Recommencer
          </button>
          <button onClick={publish} disabled={publishing} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "none", backgroundColor: "white", color: "#0a0a0a", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: publishing ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {publishing ? "Publication..." : <>Publier <Check size={16} strokeWidth={2.5} /></>}
          </button>
        </div>
      </div>,
      document.body
    );
  }
  if (step === "preview" && preview) {
    // Portail direct sur document.body — voir le commentaire de l'étape "context" plus haut.
    return ReactDOM.createPortal(
      <div style={{ position: "fixed", inset: 0, backgroundColor: "#000", zIndex: 2000, display: "flex", flexDirection: "column" }}>
        <video src={preview.url} controls autoPlay loop playsInline style={{ flex: 1, width: "100%", objectFit: "contain", backgroundColor: "#000" }} />
        <button onClick={handleCloseAttempt} aria-label="Fermer" style={{ ...iconBtnStyle, position: "absolute", top: "max(16px, env(safe-area-inset-top))", left: 16 }}><X size={20} strokeWidth={2.2} /></button>
        {cameraError && <div style={{ position: "absolute", top: 70, left: 16, right: 16, fontSize: 12.5, color: "white", backgroundColor: "rgba(192,57,43,0.9)", padding: "8px 12px", borderRadius: 10 }}>{cameraError}</div>}
        {kind === "story" && (
          <div style={{ padding: "0 16px 10px", backgroundColor: "#000" }}>
            <input value={caption} onChange={e => setCaption(e.target.value)} maxLength={150} placeholder="Une légende (optionnel)..." style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.25)", backgroundColor: "rgba(255,255,255,0.08)", color: "white", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
          </div>
        )}
        <div style={{ display: "flex", gap: 10, padding: "14px 16px max(14px, env(safe-area-inset-bottom))", backgroundColor: "#000" }}>
          <button onClick={retake} disabled={publishing} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,0.3)", backgroundColor: "transparent", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Recommencer
          </button>
          <button onClick={publish} disabled={publishing} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "none", backgroundColor: "white", color: "#0a0a0a", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: publishing ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {publishing ? "Publication..." : <>Publier <Check size={16} strokeWidth={2.5} /></>}
          </button>
        </div>
      </div>,
      document.body
    );
  }

  // Étape 2 — caméra plein écran.
  const progress = Math.min(1, recordMs / maxMs);
  const R = 34, C = 2 * Math.PI * R;

  // Portail direct sur document.body — voir le commentaire de l'étape "context" plus haut. Corrige
  // du même coup la feuille de confirmation d'abandon imbriquée (setConfirmDiscard), qui hérite du
  // vrai viewport une fois son ancêtre commun sorti du conteneur scrollable.
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "#000", zIndex: 2000, overflow: "hidden" }}>
      <video ref={videoLiveRef} muted playsInline style={{ display: "none" }} />
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />

      {showGrid && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {[33.33, 66.66].map(p => <div key={`v${p}`} style={{ position: "absolute", top: 0, bottom: 0, left: `${p}%`, width: 1, backgroundColor: "rgba(255,255,255,0.35)" }} />)}
          {[33.33, 66.66].map(p => <div key={`h${p}`} style={{ position: "absolute", left: 0, right: 0, top: `${p}%`, height: 1, backgroundColor: "rgba(255,255,255,0.35)" }} />)}
        </div>
      )}

      {!streamReady && !cameraError && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>Ouverture de la caméra...</div>
      )}
      {cameraError && (
        <div style={{ position: "absolute", top: "45%", left: 20, right: 20, textAlign: "center", color: "white", fontSize: 13, backgroundColor: "rgba(192,57,43,0.85)", padding: "10px 14px", borderRadius: 12 }}>{cameraError}</div>
      )}

      <button onClick={handleCloseAttempt} aria-label="Fermer" style={{ ...iconBtnStyle, position: "absolute", top: "max(16px, env(safe-area-inset-top))", left: 16 }}><X size={20} strokeWidth={2.2} /></button>

      <div style={{ position: "absolute", top: "max(16px, env(safe-area-inset-top))", right: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={() => setShowGrid(g => !g)} aria-label="Réglages" style={{ ...iconBtnStyle, backgroundColor: showGrid ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.4)", color: showGrid ? "#0a0a0a" : "white" }}><Settings size={19} strokeWidth={2} /></button>
        <button onClick={() => setShowFilters(f => !f)} aria-label="Filtres" style={{ ...iconBtnStyle, backgroundColor: showFilters ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.4)", color: showFilters ? "#0a0a0a" : "white" }}><SlidersHorizontal size={19} strokeWidth={2} /></button>
        {!(mode === "real" && realCapturing) && (
          <button onClick={() => !recording && setFacingMode(m => (m === "user" ? "environment" : "user"))} aria-label="Changer de caméra" style={iconBtnStyle}><RefreshCcw size={19} strokeWidth={2} /></button>
        )}
      </div>

      <div style={{ position: "absolute", left: 0, right: 0, bottom: "max(22px, env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        {showFilters && (
          <div style={{ display: "flex", gap: 14, overflowX: "auto", padding: "4px 20px", maxWidth: "100%" }}>
            {FILTERS.map(f => (
              <button key={f.id} onClick={() => setFilterId(f.id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}>
                <span style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: f.swatch, border: filterId === f.id ? "2.5px solid white" : "2.5px solid transparent", boxShadow: filterId === f.id ? "0 0 0 1.5px rgba(0,0,0,0.4)" : "none" }} />
                <span style={{ fontSize: 10.5, color: filterId === f.id ? "white" : "rgba(255,255,255,0.65)", fontWeight: filterId === f.id ? 700 : 500 }}>{f.label}</span>
              </button>
            ))}
          </div>
        )}

        {!recording && mode !== "real" && (
          <div style={{ display: "flex", alignItems: "center", backgroundColor: "rgba(255,255,255,0.16)", borderRadius: 20, padding: 3 }}>
            {(["vibe", "live"] as const).map(m => (
              <button key={m} onClick={() => setMode(m)} style={{ border: "none", cursor: "pointer", padding: "7px 18px", borderRadius: 17, backgroundColor: (mode as string) === m ? "white" : "transparent", color: (mode as string) === m ? "#0a0a0a" : "rgba(255,255,255,0.75)", fontSize: 12.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" as const, transition: "all 0.15s" }}>
                {m === "vibe" ? "Vibe · 10s" : "Live · 30s"}
              </button>
            ))}
          </div>
        )}

        {mode === "real" && !recording && (
          <button
            onClick={() => setShowMoodSheet(true)}
            aria-label="MOOD"
            style={{
              display: "flex", alignItems: "center", gap: 6, border: "none", cursor: "pointer",
              padding: "6px 12px", borderRadius: 14,
              backgroundColor: moodKey ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.12)",
              color: "white", fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
            }}
          >
            {moodKey ? (() => { const Icon = MOOD_ICONS[moodKey as keyof typeof MOOD_ICONS]; return Icon ? <Icon size={14} strokeWidth={2} /> : null; })() : null}
            {moodKey ? t(lang, moodLabelKey(moodKey)) : "MOOD"}
          </button>
        )}

        <button
          onClick={() => (mode === "real" ? captureRealShot() : (recording ? stopRecording() : startRecording()))}
          disabled={!streamReady || (mode === "real" && (realCapturing || (!!realBackShot && !!realFrontShot)))}
          aria-label={mode === "real" ? "Capturer" : (recording ? "Arrêter" : "Filmer")}
          style={{ width: 76, height: 76, borderRadius: 38, border: "4px solid white", backgroundColor: "transparent", position: "relative", cursor: streamReady ? "pointer" : "default", opacity: streamReady ? 1 : 0.4, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {recording && (
            <svg width={76} height={76} style={{ position: "absolute", inset: -4, transform: "rotate(-90deg)" }}>
              <circle cx={38} cy={38} r={R} fill="none" stroke="#e74c3c" strokeWidth={4} strokeDasharray={C} strokeDashoffset={C * (1 - progress)} strokeLinecap="round" />
            </svg>
          )}
          <span style={{ width: recording ? 26 : 60, height: recording ? 26 : 60, borderRadius: recording ? 6 : 30, backgroundColor: recording ? "#e74c3c" : "white", transition: "all 0.15s" }} />
        </button>

        {recording && <span style={{ color: "white", fontSize: 12.5, fontWeight: 700 }}>{Math.ceil((maxMs - recordMs) / 1000)}s</span>}
        {mode === "real" && realCapturing && <span style={{ color: "white", fontSize: 12.5, fontWeight: 700 }}>{t(lang, "real_vibe_capturing_selfie")}</span>}
        {mode === "real" && !realCapturing && !realBackShot && streamReady && <span style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 600 }}>{t(lang, "real_vibe_capture_hint")}</span>}
        {showMoodSheet && (
          <RealVibeMoodSheet
            lang={lang}
            moodKey={moodKey}
            wishKey={moodWishKey}
            onChange={(mk, wk) => { setMoodKey(mk); setMoodWishKey(wk); setShowMoodSheet(false); }}
            onClose={() => setShowMoodSheet(false)}
          />
        )}
      </div>

      {confirmDiscard && (
        <div onClick={() => setConfirmDiscard(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", zIndex: 2100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "#1c1c1e", borderRadius: 16, padding: "22px 20px", width: "100%", maxWidth: 320, textAlign: "center" as const }}>
            <div style={{ color: "white", fontWeight: 700, fontSize: 15.5, marginBottom: 8 }}>Abandonner ce contenu ?</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12.5, marginBottom: 18 }}>Tu perdras ce que tu viens de filmer.</div>
            <button onClick={confirmAbandon} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", backgroundColor: "transparent", color: "#e74c3c", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 4 }}>Abandonner</button>
            <div style={{ height: 1, backgroundColor: "rgba(255,255,255,0.1)", margin: "4px 0" }} />
            <button onClick={() => setConfirmDiscard(false)} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", backgroundColor: "transparent", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Continuer</button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
